<template>
  <div class="distributor-landing tw-min-h-screen" style="background-color: var(--bg-main, #0b1120);">
    <!-- ===== TOP NAVBAR ===== -->
    <header class="tw-sticky tw-top-0 tw-z-50 tw-bg-[#0b1120]/95 tw-backdrop-blur-md tw-border-b tw-border-white/10">
      <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-h-16 tw-flex tw-items-center tw-justify-between">
        <!-- Brand -->
        <div class="tw-flex tw-items-center tw-gap-3">
          <img src="@/assets/promolider_logo_2.png" alt="Promolíder" class="tw-h-7 tw-w-auto" />
          <span class="tw-text-white/40 tw-mx-1">|</span>
          <span class="tw-text-sm tw-text-gray-300">Aula Virtual</span>
        </div>

        <!-- Distributor Name Badge -->
        <div v-if="distributor" class="tw-hidden sm:tw-flex tw-items-center tw-gap-2 tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-full tw-px-4 tw-py-1.5">
          <div class="tw-w-7 tw-h-7 tw-rounded-full tw-bg-[#18d600]/20 tw-flex tw-items-center tw-justify-center tw-text-[#18d600] tw-text-xs tw-font-bold tw-flex-shrink-0">
            {{ distributorInitials }}
          </div>
          <span class="tw-text-sm tw-text-gray-300">Recomendado por <strong class="tw-text-white">{{ distributor.full_name }}</strong></span>
        </div>

        <!-- Actions -->
        <div class="tw-flex tw-items-center tw-gap-3">
          <router-link :to="{ name: 'Login' }" class="tw-text-sm tw-font-semibold tw-text-gray-300 hover:tw-text-[#18d600] tw-transition-colors">Iniciar Sesión</router-link>
          <button @click="openRegisterModal()" class="tw-bg-[#18d600] hover:tw-bg-green-600 tw-text-white tw-text-sm tw-font-bold tw-py-2 tw-px-4 tw-rounded-xl tw-transition-all tw-shadow-md hover:tw-shadow-lg">
            Registrarse
          </button>
        </div>
      </div>

      <!-- Mobile distributor badge -->
      <div v-if="distributor" class="sm:tw-hidden tw-px-4 tw-pb-2">
        <div class="tw-flex tw-items-center tw-gap-2 tw-bg-white/5 tw-border tw-border-white/10 tw-rounded-lg tw-px-3 tw-py-1.5">
          <div class="tw-w-6 tw-h-6 tw-rounded-full tw-bg-[#18d600]/20 tw-flex tw-items-center tw-justify-center tw-text-[#18d600] tw-text-[10px] tw-font-bold tw-flex-shrink-0">
            {{ distributorInitials }}
          </div>
          <span class="tw-text-xs tw-text-gray-400">Recomendado por <strong class="tw-text-white">{{ distributor.full_name }}</strong></span>
        </div>
      </div>
    </header>

    <!-- ===== MAIN CONTENT ===== -->
    <main>
      <!-- Loading State -->
      <div v-if="isLoading" class="tw-flex tw-items-center tw-justify-center tw-py-32">
        <div class="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-[#18d600]"></div>
      </div>

      <!-- Error / Not Found -->
      <div v-else-if="error" class="tw-max-w-lg tw-mx-auto tw-text-center tw-py-32 tw-px-4">
        <svg class="tw-w-16 tw-h-16 tw-mx-auto tw-text-gray-600 tw-mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path></svg>
        <h2 class="tw-text-xl tw-font-bold tw-text-white tw-mb-2">Landing no encontrada</h2>
        <p class="tw-text-gray-400">El distribuidor no existe o no tiene una landing configurada.</p>
        <router-link :to="{ name: 'home' }" class="tw-inline-block tw-mt-6 tw-bg-[#18d600] tw-text-white tw-font-bold tw-px-6 tw-py-2 tw-rounded-xl hover:tw-bg-green-600 tw-transition-colors">
          Ir al inicio
        </router-link>
      </div>

      <!-- Content -->
      <div v-else>
        <!-- Hero Banner Slider -->
        <div class="tw-w-full tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-py-6">
          <HeroBannerSlider />
        </div>

        <div class="tw-max-w-7xl tw-mx-auto tw-px-4 sm:tw-px-6 lg:tw-px-8 tw-space-y-12 tw-pb-16">

          <!-- ===== HIGHLIGHTED COURSES (Distributor's picks) ===== -->
          <section v-if="highlightedCourses.length > 0">
            <div class="tw-flex tw-items-center tw-gap-3 tw-mb-6">
              <div class="tw-w-1 tw-h-8 tw-bg-[#18d600] tw-rounded-full"></div>
              <div>
                <h2 class="tw-text-2xl tw-font-bold tw-text-white tw-font-outfit">
                  Recomendados por {{ distributor.full_name }}
                </h2>
                <p class="tw-text-gray-400 tw-text-sm tw-mt-0.5">Cursos seleccionados especialmente para ti</p>
              </div>
            </div>

            <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5 tw-gap-4">
              <div
                v-for="course in highlightedCourses"
                :key="'hl-' + course.id"
                class="highlighted-card tw-bg-white/5 tw-border tw-border-[#18d600]/20 tw-rounded-2xl tw-overflow-hidden hover:tw-border-[#18d600]/50 tw-transition-all tw-duration-300 tw-cursor-pointer tw-group"
                @click="goToCourse(course)"
              >
                <!-- Image -->
                <div class="tw-relative tw-pt-[56.25%] tw-bg-gray-800">
                  <img
                    :src="getCourseImg(course)"
                    :alt="course.title"
                    class="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-105 tw-transition-transform tw-duration-300"
                    @error="onImgError"
                  />
                  <div class="tw-absolute tw-top-2 tw-left-2">
                    <span class="tw-bg-[#18d600] tw-text-white tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-md tw-uppercase">
                      ⭐ Recomendado
                    </span>
                  </div>
                  <div class="tw-absolute tw-bottom-2 tw-right-2 tw-bg-black/70 tw-text-white tw-text-sm tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-lg">
                    {{ Number(course.price) === 0 ? 'Gratis' : '$' + course.price }}
                  </div>
                </div>

                <!-- Info -->
                <div class="tw-p-3">
                  <h4 class="tw-text-sm tw-font-bold tw-text-white tw-line-clamp-2 tw-mb-1">{{ course.title }}</h4>
                  <p class="tw-text-xs tw-text-gray-400 tw-line-clamp-2">{{ course.description }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- ===== FREE COURSES ===== -->
          <section>
            <FreeCoursesGrid />
          </section>

          <!-- ===== SEARCH BAR ===== -->
          <div class="tw-max-w-2xl tw-mx-auto">
            <div class="tw-relative">
              <span class="tw-absolute tw-inset-y-0 tw-left-4 tw-flex tw-items-center tw-pointer-events-none">
                <svg class="tw-w-5 tw-h-5 tw-text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
              </span>
              <input
                v-model="searchQuery"
                type="text"
                class="tw-w-full tw-py-3 tw-rounded-xl tw-border tw-border-gray-600 tw-bg-white/10 tw-text-white focus:tw-ring-2 focus:tw-ring-[#18d600] focus:tw-border-transparent tw-transition-all tw-placeholder-gray-400"
                style="padding-left: 2.75rem; padding-right: 1rem;"
                placeholder="Busca por nombre de curso o libro..."
              />
            </div>
          </div>

          <!-- ===== FULL CATALOG ===== -->
          <section>
            <h3 class="tw-text-2xl tw-font-bold tw-text-white tw-font-outfit tw-mb-2">Catálogo Completo</h3>
            <p class="tw-text-gray-400 tw-text-sm tw-mb-6">Explora todos nuestros cursos y libros.</p>

            <div v-if="filteredCatalog.length > 0" class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-4 xl:tw-grid-cols-5 tw-gap-4">
              <Card
                v-for="course in filteredCatalog"
                :key="'cat-' + course.id"
                :course="course"
                :cardType="1"
                :categories="categories"
                viewMode="marketplace"
              />
            </div>
            <div v-else class="tw-text-center tw-text-gray-500 tw-py-8">
              No se encontraron resultados para "{{ searchQuery }}".
            </div>
          </section>
        </div>
      </div>
    </main>

    <!-- ===== FOOTER ===== -->
    <FooterV />

    <!-- ===== QUICK REGISTER MODAL ===== -->
    <QuickRegisterModal
      :show="showRegisterModal"
      type="free"
      :refId="distributor ? String(distributor.id) : '1'"
      @close="showRegisterModal = false"
      @success="handleRegisterSuccess"
    />
  </div>
</template>

<script>
import HeroBannerSlider from '@/components/Student/dashboard/HeroBannerSlider';
import FreeCoursesGrid from '@/components/Student/dashboard/FreeCoursesGrid';
import Card from '@/components/courses/cards/marketplace.vue';
import FooterV from '@/components/Footer/FooterV.vue';
import QuickRegisterModal from '@/components/QuickRegisterModal.vue';
import { authGet } from '@/helpers/authStorage';

export default {
  name: 'DistributorLanding',
  components: {
    HeroBannerSlider,
    FreeCoursesGrid,
    Card,
    FooterV,
    QuickRegisterModal
  },
  data() {
    return {
      isLoading: true,
      error: false,
      distributor: null,
      highlightedCourses: [],
      catalogCourses: [],
      categories: [],
      searchQuery: '',
      showRegisterModal: false,
    };
  },
  computed: {
    distributorInitials() {
      if (!this.distributor) return '';
      const n = (this.distributor.name || '').charAt(0).toUpperCase();
      const l = (this.distributor.last_name || '').charAt(0).toUpperCase();
      return n + l;
    },
    filteredCatalog() {
      if (!this.searchQuery.trim()) return this.catalogCourses;
      const term = this.searchQuery.toLowerCase();
      return this.catalogCourses.filter(c =>
        (c.title && c.title.toLowerCase().includes(term)) ||
        (c.description && c.description.toLowerCase().includes(term))
      );
    }
  },
  methods: {
    async fetchLandingData() {
      try {
        const username = this.$route.params.username;

        // 1. Fetch distributor info + highlighted courses
        const landingRes = await this.axios.get(`public/landing/${username}`);
        if (landingRes.data && landingRes.data.data) {
          this.distributor = landingRes.data.data.distributor;
          this.highlightedCourses = landingRes.data.data.highlighted_courses || [];

          // Save referral info in localStorage
          localStorage.setItem('ref_distributor_id', this.distributor.id);
          localStorage.setItem('ref_distributor_username', this.distributor.username);
        }

        // 2. Fetch full catalog (same endpoint as guest dashboard)
        const catalogRes = await this.axios.get('course/related-courses');
        if (catalogRes.data && Array.isArray(catalogRes.data.data)) {
          this.catalogCourses = catalogRes.data.data;
        }

        // 3. Fetch categories for Card component
        try {
          const catRes = await this.axios.get('category/list');
          this.categories = catRes.data.data || [];
        } catch (e) {
          this.categories = [];
        }

      } catch (err) {
        console.error('Error loading distributor landing:', err);
        this.error = true;
      } finally {
        this.isLoading = false;
      }
    },
    goToCourse(course) {
      const slug = course.slug || course.title.toLowerCase().replace(/\s+/g, '-');
      const routeName = course.type === 'book' ? 'buy-books' : 'buy-cursos';

      // Check if logged in
      if (authGet('access_token')) {
        this.$router.push({ name: routeName, params: { ide: course.id, slug } });
      } else {
        // Open register modal, after success redirect
        this.pendingCourse = course;
        this.showRegisterModal = true;
      }
    },
    openRegisterModal() {
      this.pendingCourse = null;
      this.showRegisterModal = true;
    },
    getCourseImg(course) {
      const url = course.img || course.url_portada || course.image;
      if (!url) return require("@/assets/background-login.webp");
      if (url.startsWith("http")) return url.replace("s3.sa-east-1", "s3-accelerate");
      return "https://promolider-storage-user.s3-accelerate.amazonaws.com/" + url;
    },
    onImgError(e) {
      if (e?.target) {
        e.target.src = require("@/assets/background-login.webp");
      }
    },
    handleRegisterSuccess() {
      this.showRegisterModal = false;
      if (this.pendingCourse) {
        const course = this.pendingCourse;
        const slug = course.slug || course.title.toLowerCase().replace(/\s+/g, '-');
        const routeName = course.type === 'book' ? 'buy-books' : 'buy-cursos';
        this.$router.push({ name: routeName, params: { ide: course.id, slug } });
      } else {
        // Redirect to home/dashboard
        this.$router.push({ name: 'home' });
      }
    },
  },
  created() {
    // If already logged in, they can browse but the ref is still saved
    if (this.$route.params.username) {
      this.fetchLandingData();
    } else {
      this.error = true;
      this.isLoading = false;
    }
  }
};
</script>

<style scoped>
.highlighted-card:hover {
  box-shadow: 0 0 20px rgba(24, 214, 0, 0.15);
  transform: translateY(-2px);
}
</style>
